<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: isSmallScreen
      }
    ]"
  >
    <div
      :class="[
        $style.main,
        {
          [$style.hasDragIcon]: rowAmount > 1
        }
      ]"
    >
      <div :class="$style.formFieldsWrap">
        <div :class="$style.formSection">
          <div
            v-if="hasDeletedService"
            v-test="'part-service-disabled'"
          >
            <div>
              <BaseInputLabel :text="$t('global.items.service', 1)" />
            </div>
            <div :class="$style.disabledService">
              {{ selectedService.name }}
            </div>
          </div>
          <BaseDropdown
            v-else-if="services.length"
            v-model="serviceDropdownValue"
            :options="serviceDropdownOptions"
            :warning="
              unavailableService
                ? {
                    icon: 'calendar',
                    color: 'warning',
                    tooltip:
                      unavailableService.reason === 'MAX'
                        ? $t('service.availability.calendar_tooltip_max')
                        : $t('service.availability.calendar_tooltip_times')
                  }
                : null
            "
            :groups="dropdownGroups"
            :label="$t('global.items.service', 1)"
            searchable
            searchAutoFocus
            breakLines
            required
            :disabled="hasDeletedService || onlyResource"
            @update:modelValue="onServiceSelected"
            v-test="'part-service'"
          />
          <BaseSpinner
            v-else
            line
          />
        </div>
        <div
          v-if="!servicePackage"
          :class="[
            $style.formSection,
            {
              [$style.isSmall]: !selectedService?.requiresProcessingTime
            }
          ]"
        >
          <PartDuration
            :part="part"
            :requiresProcessingTime="!!selectedService?.requiresProcessingTime"
            :disabled="onlyResource"
            showLabel
            v-test="'part-duration'"
          />
        </div>
        <PackageServices
          v-if="servicePackage"
          :partIndexes="partIndexes"
          :onlyResource="onlyResource"
        />
        <PartResources
          v-else
          :part="part"
          :partIndex="partIndexes[0]"
        />
        <BaseText
          v-if="servicePackage"
          inline
          @click.stop="onSplit"
          v-test="'package-part-split'"
        >
          {{ $t('appointment.create.split_into_services') }}
        </BaseText>
      </div>
      <BaseAlert
        v-if="hasDeletedService"
        color="warning"
        :text="$t('appointment.service_deleted_message')"
        inline
        v-test="'service-deleted-message'"
      />
      <ServiceSuggestions
        v-if="!part.serviceId"
        :part="part"
        @select="selectSuggestedService"
      />
    </div>
    <div
      v-show="rowAmount > 1"
      :class="$style.dragIcon"
      class="js-drag-handle"
      v-test="'appointment-part-draghandle'"
    >
      <BaseIcon name="drag" />
    </div>
  </div>
</template>

<script lang="ts">
import { storeToRefs } from 'pinia';
import type { PropType } from 'vue';
import { computed, defineComponent } from 'vue';
import filters from '@/filters';
import {
  isSmallScreen,
  setPartDurations,
} from '@/modules/calendar/appointment/helpers.ts';
import { useCreateAppointmentStore } from '@/stores/calendar-create-appointment';
import { useServicesStore } from '@/stores/services';
import type {
  AppointmentPartAttributes,
  Service,
  ServicePackage,
} from '@/types';
import { usePartRows } from '../../part-rows';
import PackageServices from './PackageServices.vue';
import PartDuration from './PartDuration.vue';
import PartResources from './PartResources.vue';
import ServiceSuggestions from './ServiceSuggestions.vue';
import { useServiceDropdownOptions } from './service-dropdown-options';

export default defineComponent({
  name: 'AppointmentPartContent',
  components: {
    ServiceSuggestions,
    PackageServices,
    PartDuration,
    PartResources,
  },
  props: {
    part: {
      type: Object as PropType<AppointmentPartAttributes>,
      required: true,
    },
    // The parts shown by this card: one for a plain part, several for a package
    partIndexes: {
      type: Array as PropType<number[]>,
      required: true,
    },
    onlyResource: {
      type: Boolean,
      default: false,
      required: false,
    },
    servicePackage: {
      type: Object as PropType<ServicePackage>,
      required: false,
      default: undefined,
    },
  },
  setup(props) {
    const {
      formData,
      existingAppointmentData,
      loadAllocations,
      selectServiceOrPackage,
      splitPackageParts,
    } = useCreateAppointmentStore();
    const { services, unavailableServices } = storeToRefs(
      useCreateAppointmentStore(),
    );
    const unavailableService = computed(() =>
      unavailableServices.value.find((s) => s.id === props.part.serviceId),
    );

    const { dropdownOptions, dropdownGroups } = useServiceDropdownOptions();
    const { rows } = usePartRows();
    const rowAmount = computed(() => rows.value.length);

    return {
      formData,
      services,
      existingAppointmentData,
      loadAllocations,
      selectServiceOrPackage,
      splitPackageParts,
      filters,
      unavailableService,
      isSmallScreen,
      dropdownOptions,
      dropdownGroups,
      rowAmount,
    };
  },
  computed: {
    hasDeletedService() {
      return !!this.selectedService?.deleted;
    },
    selectedService(): Service {
      return this.services.find(
        (service: Service) => service.id === this.part.serviceId,
      );
    },
    serviceDropdownOptions() {
      return this.dropdownOptions(this.services);
    },
    serviceDropdownValue: {
      get(): number {
        return this.servicePackage
          ? -this.servicePackage.id
          : this.part.serviceId;
      },
      set(value: number) {
        this.part.serviceId = value;
      },
    },
  },
  methods: {
    onSplit() {
      this.splitPackageParts(this.partIndexes);
    },
    selectSuggestedService(serviceId: Service['id']) {
      this.part.serviceId = serviceId;
      this.setPartData();
    },
    onServiceSelected(value: number) {
      // Picking a package (a negative value, see service-dropdown-options.ts), or picking
      // anything on a package card, replaces this card's parts with new ones through the store.
      // A plain service picked on a plain part just updates that part.
      if (value < 0 || this.servicePackage) {
        this.selectServiceOrPackage(this.partIndexes, value);
      } else {
        this.setPartData();
      }
    },
    setPartData() {
      // Set allocations and part durations based on the service
      this.loadAllocations(this.part);
      this.setPartDurations(this.part);
    },
    setPartDurations(part: AppointmentPartAttributes) {
      const { serviceById } = useServicesStore();
      const service = part.serviceId ? serviceById(part.serviceId) : null;

      // When a new service is selected, the part durations are set to those of the service
      if (service) {
        setPartDurations(part, service);
      }
    },
  },
  created() {
    // Parts that already have their requirements loaded (e.g. after splitting a package, which remounts the card)
    // are already set up, and running setPartData again would overwrite any durations the user edited
    const isPartLoaded = this.part.allocationsAttributes.some(
      (allocation) => !!allocation.requirementId,
    );

    if (
      this.part.serviceId &&
      !this.existingAppointmentData.id &&
      !isPartLoaded
    ) {
      // When creating a new appointment, it is possible that the serviceIds on allocations are already set
      // This happens for example when creating an appointment from the waiting list
      // In this case, we need to load the correct allocations
      // Don't do this when editing an existing appointment

      this.setPartData();
    }
  },
});
</script>

<style
  lang="scss"
  module
>
$form-fields-gap: $spacing * 0.75;

.base {
  position: relative;
  display: flex;
  gap: $spacing * 0.75;
  padding: $spacing * 0.75;
}

.main {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: $spacing * 0.75;

  // Leave room for the drag icon, which only shows when there are multiple parts
  &.hasDragIcon {
    margin-right: $spacing * 1.4;
  }
}

.formFieldsWrap {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: $form-fields-gap;
}

.formSection {
  .base:not(.smallScreen) & {
    &:not(.isSmall) {
      width: 33%;
    }

    &.isSmall {
      width: 30%;
    }
  }

  .base.smallScreen & {
    width: 100%;
  }

  &:empty {
    display: none;
  }
}

.disabledService {
  display: flex;
  align-items: center;
  height: $input-height;
}

.dragIcon {
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  right: $spacing * 0.5;
  bottom: $spacing * 0.75;
  cursor: move;
}
</style>
